import React, { useState, useEffect, useRef } from "react";
import "./LanguageSelector.css";

const LANGUAGES = [
  { code: "en", label: "English", native: "English", flag: "🇬🇧" },
  { code: "hi", label: "Hindi", native: "हिन्दी", flag: "🇮🇳" },
  { code: "ta", label: "Tamil", native: "தமிழ்", flag: "🇮🇳" },
  { code: "ml", label: "Malayalam", native: "മലയാളം", flag: "🇮🇳" },
  { code: "kn", label: "Kannada", native: "ಕನ್ನಡ", flag: "🇮🇳" },
  { code: "te", label: "Telugu", native: "తెలుగు", flag: "🇮🇳" },
];

function setLanguageCookies(langCode) {
  if (langCode === "en") {
    const past = "Thu, 01 Jan 1970 00:00:00 UTC";
    document.cookie = `googtrans=; path=/; expires=${past};`;
    try {
      const host = window.location.hostname;
      if (host) {
        document.cookie = `googtrans=; path=/; domain=.${host}; expires=${past};`;
        const parts = host.split(".");
        if (parts.length > 2) {
          document.cookie = `googtrans=; path=/; domain=.${parts.slice(-2).join(".")}; expires=${past};`;
        }
      }
    } catch (e) {}
    return;
  }

  const cookieVal = `/en/${langCode}`;
  document.cookie = `googtrans=${cookieVal}; path=/;`;
  try {
    const host = window.location.hostname;
    if (host && host !== "localhost" && host !== "127.0.0.1") {
      document.cookie = `googtrans=${cookieVal}; path=/; domain=.${host};`;
      const parts = host.split(".");
      if (parts.length > 2) {
        document.cookie = `googtrans=${cookieVal}; path=/; domain=.${parts.slice(-2).join(".")};`;
      }
    }
  } catch (e) {}
}

function triggerCombo(combo, langCode) {
  if (!combo) return false;
  try {
    combo.value = langCode;
    combo.dispatchEvent(new Event("change", { bubbles: true }));
    if (typeof combo.onchange === "function") {
      combo.onchange();
    }
    return true;
  } catch (e) {
    console.warn("Failed to trigger combo change:", e);
    return false;
  }
}

export function setGoogleTranslateLanguage(langCode) {
  if (langCode === "en") {
    setLanguageCookies("en");
    const combo = document.querySelector(".goog-te-combo");
    if (combo) {
      triggerCombo(combo, "en");
    }
    // Restore English cleanly if the page was translated
    if (
      document.documentElement.classList.contains("translated-ltr") ||
      document.documentElement.classList.contains("translated-rtl") ||
      document.querySelector(".goog-te-banner-frame")
    ) {
      window.location.reload();
    }
    return;
  }

  setLanguageCookies(langCode);
  const combo = document.querySelector(".goog-te-combo");
  if (combo) {
    triggerCombo(combo, langCode);
  } else {
    // Poll briefly for combo in case Google Translate element script is still initializing
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      const lateCombo = document.querySelector(".goog-te-combo");
      if (lateCombo) {
        clearInterval(interval);
        triggerCombo(lateCombo, langCode);
      } else if (attempts >= 8) {
        clearInterval(interval);
        // Fallback: reload with cookie set so Translate element translates on load
        window.location.reload();
      }
    }, 150);
  }
}

export default function LanguageSelector() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(LANGUAGES[0]);
  const dropdownRef = useRef(null);

  // Detect current language from cookie on mount
  useEffect(() => {
    try {
      const match = document.cookie.match(/googtrans=\/(?:en|auto)\/([a-z]+)/i);
      if (match && match[1]) {
        const lang = LANGUAGES.find((l) => l.code.toLowerCase() === match[1].toLowerCase());
        if (lang) setSelected(lang);
      }
    } catch (e) {}
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleSelect(lang) {
    setSelected(lang);
    setOpen(false);
    setGoogleTranslateLanguage(lang.code);
  }

  return (
    <div className="lang-selector" ref={dropdownRef}>
      <button
        className="lang-trigger"
        onClick={() => setOpen((p) => !p)}
        title="Select Language"
      >
        <span className="lang-globe">🌐</span>
        <span className="lang-current">{selected.native}</span>
        <span className={`lang-chevron ${open ? "open" : ""}`}>▾</span>
      </button>

      {open && (
        <div className="lang-dropdown">
          <div className="lang-dropdown-header">Select Language</div>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`lang-option ${selected.code === lang.code ? "active" : ""}`}
              onClick={() => handleSelect(lang)}
            >
              <span className="lang-flag">{lang.flag}</span>
              <span className="lang-names">
                <span className="lang-native">{lang.native}</span>
                <span className="lang-english">{lang.label}</span>
              </span>
              {selected.code === lang.code && <span className="lang-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
